'use client'

import { Skeleton } from '@brew/ui/skeleton'
import dynamic from 'next/dynamic'

import { createPreloadableComponent } from '@/lib/react/create-preloadable-component'

import type { EmailJsxPreviewProps } from './email-jsx-preview'

export type { EmailJsxPreviewProps } from './email-jsx-preview'

/**
 * Thin dynamic shim over `email-jsx-preview.tsx` so the tiptap viewer
 * runtime (@tiptap/react + @react-email/editor plugins + prosemirror —
 * order of 100KB+ gz) loads as its own chunk instead of shipping in the
 * importing route's initial bundle. The template detail page (public +
 * app) reaches this as `EmailDesignPreview`'s authored lane; the editor
 * emits no server markup anyway (`immediatelyRender: false`), so
 * `ssr: false` changes nothing at SSR time — only when the chunk
 * downloads. Fallback holds vertical space so the card doesn't jump when
 * the chunk lands.
 *
 * Preloadable: the templates grid downloads the chunk ahead of a card
 * click (`preloadEmailJsxPreview`), so the detail page renders the preview
 * directly instead of its loading frame.
 */
const EmailJsxPreviewBody = createPreloadableComponent<EmailJsxPreviewProps>(
  () => import('./email-jsx-preview').then((m) => m.EmailJsxPreview),
  dynamic(
    () =>
      import('./email-jsx-preview').then((m) => ({
        default: m.EmailJsxPreview,
      })),
    {
      ssr: false,
      loading: () => <Skeleton className="h-[480px] w-full" />,
    }
  )
)

/** Download the preview chunk ahead of a template detail visit. */
export const preloadEmailJsxPreview = EmailJsxPreviewBody.preload

export function EmailJsxPreview(props: EmailJsxPreviewProps) {
  const Preview = EmailJsxPreviewBody.useLatchedComponent()
  return Preview ? <Preview {...props} /> : null
}
